import type { ExternalTableSchema } from '@packages/table-builder';
import { accountStatusOptions, EmailStatusOptions } from '@/constants/common';

export const participantApprovalTable: ExternalTableSchema = {
  metadata: {
    title: 'Participant Approval',
    entity: 'ParticipantApproval',
    description: 'Pending participant approval queue.',
  },

  dataSource: {
    endpoint: '/api/customer/api/app/participant-approval/participants-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
      ApprovalStatusIds: 'filters.ApprovalStatusIds',
      EmployeeType: 'filters.EmployeeType',
      AccountStatus: 'filters.AccountStatus',
      IsEmailSend: 'filters.IsEmailSend',
      BrokerAccountStatusIds: '',
      EmployeeStatus: '',
    },
    filters: [
      {
        name: 'ApprovalStatusIds',
        semanticId: 'employeeStatusId',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/customer/api/app/participant-approval/participant-statuses',
          labelField: 'statusName',
          valueField: 'id',
        },
      },
      {
        name: 'EmployeeType',
        semanticId: 'employeeType',
        placeholder: 'Employee Type',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/customer/api/app/employee-type/employee-types-list',
          labelField: 'name',
          valueField: 'name',
        },
      },
      {
        name: 'AccountStatus',
        semanticId: 'accountStatus',
        placeholder: 'Account Status',
        type: 'select',
        options: accountStatusOptions.map((o) => ({ label: o.label, value: o.value })),
      },
      {
        name: 'IsEmailSend',
        semanticId: 'emailStatus',
        placeholder: 'Invitation Status',
        type: 'select',
        options: EmailStatusOptions.map((o) => ({ label: o.label, value: o.value })),
      },
    ],
  },

  columns: [
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 200,
      couple: {
        titleField: 'participantName',
        subtitleField: 'employeeId',
        showTag: true,
      },
    },
    {
      field: 'employeeType',
      header: 'Employee Type',
      type: 'text',
      sortable: true,
      width: 200,
    },
    {
      field: 'creationTime',
      header: 'Created Date',
      type: 'dateTime',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'emailAddress',
      header: 'Email',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'employeeStatus',
      header: 'Employee Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: { Active: 'info', Terminated: 'error' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'country',
      header: 'Country',
      type: 'text',
      sortable: true,
      width: 200,
      align: 'center',
    },
    {
      field: 'accountStatus',
      header: 'Account Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      renderer: 'releaseTaxCertification',
      badge: {
        statusMap: { REGISTERED: 'success', UNREGISTERED: 'error' },
        defaultVariant: 'info',
      },
      conditionalValues: {
        REGISTERED: 'REGISTERED',
        UNREGISTERED: 'UNREGISTERED',
      },
    },
    {
      field: 'statusName',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: { Approved: 'success', Rejected: 'error', Pending: 'info' },
        defaultVariant: 'info',
      },
    },
    {
      field: 'taxCertification',
      header: 'Tax Certification',
      type: 'badge',
      sortable: false,
      width: 200,
      align: 'center',
      uppercase: true,
      visibility: 'picker',
      renderer: 'releaseTaxCertification',
      badge: {
        statusMap: { UNCERTIFIED: 'error', CERTIFIED: 'success', 'NOT REQUIRED': 'info' },
        defaultVariant: 'info',
      },
      conditionalValues: {
        UNCERTIFIED: 'UNCERTIFIED',
        CERTIFIED: 'CERTIFIED',
        'NOT REQUIRED': 'NOT REQUIRED',
      },
    },
    {
      field: 'brokerAccountStatusId',
      header: 'Broker Account Status',
      type: 'badge',
      sortable: false,
      width: 200,
      align: 'center',
      visibility: 'picker',
      badge: {
        statusMap: { 1: 'info', 2: 'info', 3: 'success', 4: 'success', 5: 'error' },
        defaultVariant: 'info',
      },
      conditionalValues: {
        1: 'DRAFT',
        2: 'PENDING BROKER',
        3: 'BROKER RECEIVED',
        4: 'BROKER CREATED',
        5: 'BROKER REJECTED',
      },
    },
    {
      field: 'isEmailSent',
      header: 'Invitation Send',
      type: 'badge',
      sortable: true,
      width: 200,
      align: 'center',
      badge: {
        statusMap: { true: 'success', false: 'info' },
        defaultVariant: 'info',
      },
      conditionalValues: { true: 'Sent', false: 'Not Sent' },
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_PARTICIPANT_APPROVAL', label: 'View' },
      { id: 'APPROVE_PARTICIPANT_APPROVAL', label: 'Approve' },
      { id: 'REJECT_PARTICIPANT_APPROVAL', label: 'Reject', variant: 'danger' },
      { id: 'VIEW_REMARKS_PARTICIPANT_APPROVAL', label: 'View Comments' },
    ],
    bulk: [
      { id: 'BULK_APPROVE_PARTICIPANT_APPROVAL', label: 'Approve' },
      { id: 'BULK_REJECT_PARTICIPANT_APPROVAL', label: 'Reject', variant: 'danger' },
    ],
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    selection: {
      keyField: 'id',
      selectable: { field: 'statusName', operator: 'EQUAL', value: 'Pending' },
    },
    columnPreferences: { persistenceKey: 'Participant Approval', resizable: true, reorderable: true },
  },
};
